import { useEffect, useState } from "react";
import type { Project } from "@/features/leaderboard/types/index.ts";
import { usePeerCohort } from "../hooks/use-peer-cohort.ts";
import type { CategoryFamily } from "../types/index.ts";
import type {
  CohortLens,
  CohortLensAvailability,
  NeighborEntry,
  PeerCohortResponse,
  PositioningBlock,
  SubScoreDiffEntry,
  SubScoreKey,
  TrajectoryBlock,
} from "../types/peer-cohort.ts";
import { ProjectNameLink } from "./ProjectNameLink.tsx";

// Keep in sync with FAMILY_COLORS in ScoreFundingScatter.tsx.
const FAMILY_COLORS: Record<CategoryFamily, string> = {
  DeFi: "#8b5cf6",
  Infra: "#f59e0b",
  "AI & DePIN": "#06b6d4",
  Payments: "#0f766e",
  "Tools & Exchange": "#14b8a6",
  "Gaming & NFT": "#ec4899",
  "Prediction & Betting": "#e9d5ff",
  "Privacy & Identity": "#475569",
  SocialFi: "#3b82f6",
  RWA: "#3730a3",
  Other: "#6b7280",
};

const LENS_LABELS: Record<CohortLens, string> = {
  twins: "Twins",
  "same-score": "Same score",
  "same-category": "Same category",
  "co-investors": "Co-investors",
};

const LENS_ORDER: CohortLens[] = ["twins", "same-score", "same-category", "co-investors"];

const SUB_SCORE_LABELS: Record<SubScoreKey, string> = {
  social: "Social",
  backers: "Backers",
  pedigree: "Pedigree",
  momentum: "Momentum",
  dev: "Dev",
  revenue: "Revenue",
};

interface PeerCohortPanelProps {
  projectId: string | null | undefined;
  onOpenProjectDetail?: (project: Project) => void;
}

export function PeerCohortPanel({ projectId, onOpenProjectDetail }: PeerCohortPanelProps) {
  const [lens, setLens] = useState<CohortLens>("twins");
  // Render-time reset on projectId change (the React-recommended
  // pattern over a useEffect with setState — see React docs:
  // "Resetting all state when a prop changes"). Without this, lens
  // state leaks across project transitions (e.g. user picks
  // Co-Investors on project A, clicks a neighbor with <3 investors,
  // the disabled-co-investors chip stays selected and misleads).
  //
  // Critical: we use `activeLens` (not `lens`) for the query in this
  // render. setState during render schedules a re-render, but the
  // current render still runs to completion. If we used `lens` here,
  // the first render after a projectId change would fire a stale
  // query with the previous project's lens before the re-render
  // corrects to "twins".
  const [trackedProjectId, setTrackedProjectId] = useState(projectId);
  let activeLens = lens;
  if (trackedProjectId !== projectId) {
    setTrackedProjectId(projectId);
    setLens("twins");
    activeLens = "twins";
  }

  const { data, isLoading, error } = usePeerCohort(projectId, activeLens);

  // If the requested lens is unavailable for this project (e.g. twins
  // when family is null), auto-switch to the first available lens.
  // Avoids the misleading "cohort too small" empty state when the
  // real cause is "lens-unavailable".
  //
  // Guard against a stale-data race: when projectId changes, the
  // tracked-projectId render-time reset above flips `lens` to "twins"
  // but the in-flight `data` from the OLD project is still attached
  // here for one render. Only act on `data` once it's for the current
  // projectId. (The hook's queryKey ensures `data` follows projectId
  // once the new query resolves.)
  useEffect(() => {
    if (!data) return;
    if (trackedProjectId !== projectId) return;
    const current = data.available.find((a) => a.lens === lens);
    if (current && !current.available) {
      const fallback = data.available.find((a) => a.available);
      if (fallback) setLens(fallback.lens);
    }
  }, [data, lens, projectId, trackedProjectId]);

  return (
    <section className="peer-cohort-section">
      <header className="peer-cohort-section__header">
        <div>
          <div className="section-label">Comparables</div>
          <p className="peer-cohort-section__subtitle">
            How this project sits in its peer cohort across positioning, residual, and 90-day
            trajectory.
          </p>
        </div>
      </header>

      <LensChipRow
        active={activeLens}
        availability={data?.available ?? []}
        onChange={setLens}
        loading={isLoading}
      />

      {isLoading && <div className="peer-cohort-section__loading">Loading cohort…</div>}

      {error && !data && (
        <div className="peer-cohort-section__error">Failed to load peer cohort.</div>
      )}

      {data && <PeerCohortBody data={data} onOpenProjectDetail={onOpenProjectDetail} />}
    </section>
  );
}

// ─── Filter chip row ────────────────────────────────────────────────

interface LensChipRowProps {
  active: CohortLens;
  availability: CohortLensAvailability[];
  onChange: (lens: CohortLens) => void;
  loading: boolean;
}

function LensChipRow({ active, availability, onChange, loading }: LensChipRowProps) {
  return (
    <div className="peer-cohort-chips" role="tablist" aria-label="Cohort lens">
      {LENS_ORDER.map((lens) => {
        const avail = availability.find((a) => a.lens === lens);
        const enabled = avail?.available !== false;
        const isActive = active === lens;
        return (
          <button
            key={lens}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={!enabled || loading}
            title={!enabled && avail?.reason ? avail.reason : undefined}
            className={`peer-cohort-chip${isActive ? " peer-cohort-chip--active" : ""}${
              !enabled ? " peer-cohort-chip--disabled" : ""
            }`}
            onClick={() => enabled && onChange(lens)}
          >
            {LENS_LABELS[lens]}
          </button>
        );
      })}
    </div>
  );
}

// ─── Body ───────────────────────────────────────────────────────────

interface PeerCohortBodyProps {
  data: PeerCohortResponse;
  onOpenProjectDetail?: (project: Project) => void;
}

function PeerCohortBody({ data, onOpenProjectDetail }: PeerCohortBodyProps) {
  if (!data.cohort) {
    // Distinguish three null-cohort cases so the empty state copy
    // matches reality:
    //   1. All lenses unavailable → unscored project
    //   2. Current lens unavailable but some others are → about to
    //      auto-switch via useEffect; render a transient loading
    //      state instead of "too small" (which is misleading)
    //   3. Otherwise → genuinely small cohort for the chosen lens
    const allUnavailable = data.available.length > 0 && data.available.every((a) => !a.available);
    if (allUnavailable) {
      return (
        <div className="peer-cohort-empty">
          <strong>Not yet scored.</strong> Comparables will appear once this project completes its
          first audit.
        </div>
      );
    }
    const currentLensUnavailable =
      data.available.find((a) => a.lens === data.lens)?.available === false;
    if (currentLensUnavailable) {
      return <div className="peer-cohort-section__loading">Switching to an available lens…</div>;
    }
    return (
      <div className="peer-cohort-empty">
        <strong>Cohort too small.</strong> Try a different lens — there aren't enough comparable
        projects to draw a confident signal from this slice.
      </div>
    );
  }

  return (
    <>
      <CohortSummary meta={data.cohort} positioning={data.positioning} />
      {data.positioning && <SubScoreDiffBars diffs={data.positioning.subScoreDiff} />}
      {data.trajectory && (
        <TrajectorySparkline
          trajectory={data.trajectory}
          familyColor={data.cohort.family ? FAMILY_COLORS[data.cohort.family] : FAMILY_COLORS.Other}
        />
      )}
      {data.neighbors.length > 0 && (
        <NearestNeighbors neighbors={data.neighbors} onOpenProjectDetail={onOpenProjectDetail} />
      )}
    </>
  );
}

// ─── Cohort summary ─────────────────────────────────────────────────

function CohortSummary({
  meta,
  positioning,
}: {
  meta: PeerCohortResponse["cohort"];
  positioning: PositioningBlock | null;
}) {
  if (!meta) return null;
  return (
    <div className="peer-cohort-summary">
      <div className="peer-cohort-summary__row">
        <span className="peer-cohort-summary__label">{meta.label || "Cohort"}</span>
        <span className="peer-cohort-summary__count">
          {meta.size} peer{meta.size === 1 ? "" : "s"}
        </span>
        {meta.lowSample && (
          <span className="peer-cohort-summary__badge peer-cohort-summary__badge--warn">
            low sample
          </span>
        )}
        {meta.fallbackApplied && (
          <span
            className="peer-cohort-summary__badge"
            title={
              meta.expansionReason === "stealth"
                ? "No disclosed raise — cohort matched on family + score band only"
                : "Initial Twins cohort had fewer than 5 peers — widened to family + score band"
            }
          >
            {meta.expansionReason === "stealth" ? "stealth (no raise)" : "expanded cohort"}
          </span>
        )}
      </div>
      <div className="peer-cohort-summary__metrics">
        <span>
          Median score <strong>{meta.medianScore.toFixed(1)}</strong>
        </span>
        {meta.medianRaiseUsd != null && (
          <span>
            Median raise <strong>{formatDollars(meta.medianRaiseUsd)}</strong>
          </span>
        )}
        {positioning && (
          <span>
            Rank <strong>#{positioning.rankInCohort}</strong> (
            {percentileLabel(positioning.percentileInCohort)})
          </span>
        )}
        {positioning?.residualLabel && (
          <span className={residualClass(positioning.residualUsd)}>
            {positioning.residualLabel}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Sub-score diff bars (signed, raw points) ───────────────────────

const BAR_WIDTH = 320;
const BAR_HEIGHT = 18;
const BAR_VPAD = 6;
const BAR_LABEL_W = 80;
// Wide enough for "+12.0 (18.0 vs 6.0)" at 12px font (~22 chars × 7px).
// Earlier value of 80px clipped the parenthetical detail.
const BAR_VALUE_W = 170;

function SubScoreDiffBars({ diffs }: { diffs: SubScoreDiffEntry[] }) {
  // Symmetric scale: max(|delta|) sets the half-axis. Min 5 pts to
  // keep small deltas visually meaningful.
  const maxAbs = Math.max(5, ...diffs.map((d) => Math.abs(d.delta)));
  const halfWidth = BAR_WIDTH / 2;
  const totalH = diffs.length * (BAR_HEIGHT + BAR_VPAD);

  return (
    <div className="peer-cohort-diff">
      <div className="peer-cohort-diff__title">Sub-scores vs cohort median</div>
      <svg
        className="peer-cohort-diff__svg"
        width={BAR_LABEL_W + BAR_WIDTH + BAR_VALUE_W}
        height={totalH + 8}
        viewBox={`0 0 ${BAR_LABEL_W + BAR_WIDTH + BAR_VALUE_W} ${totalH + 8}`}
        preserveAspectRatio="xMinYMid meet"
        role="img"
        aria-label="Sub-score deltas vs cohort median"
      >
        {/* Center axis */}
        <line
          x1={BAR_LABEL_W + halfWidth}
          y1={0}
          x2={BAR_LABEL_W + halfWidth}
          y2={totalH}
          stroke="#374151"
          strokeWidth={1}
        />
        {diffs.map((d, i) => {
          const y = i * (BAR_HEIGHT + BAR_VPAD);
          const barLen = (Math.abs(d.delta) / maxAbs) * halfWidth;
          const isPositive = d.delta > 0;
          const barX = BAR_LABEL_W + halfWidth - (isPositive ? 0 : barLen);
          const fill = isPositive ? "#10b981" : d.delta < 0 ? "#ef4444" : "#6b7280";
          const valueText =
            d.delta === 0 ? "—" : `${isPositive ? "+" : "−"}${Math.abs(d.delta).toFixed(1)}`;
          return (
            <g key={d.key}>
              <text
                x={BAR_LABEL_W - 8}
                y={y + BAR_HEIGHT / 2}
                textAnchor="end"
                dominantBaseline="middle"
                className="peer-cohort-diff__label"
                fontSize={12}
                fill="#9ca3af"
              >
                {SUB_SCORE_LABELS[d.key]}
              </text>
              <rect x={barX} y={y} width={barLen} height={BAR_HEIGHT} fill={fill} rx={2} />
              <text
                x={BAR_LABEL_W + BAR_WIDTH + 6}
                y={y + BAR_HEIGHT / 2}
                dominantBaseline="middle"
                className="peer-cohort-diff__value"
                fontSize={12}
                fill="#e5e7eb"
              >
                {valueText} ({d.thisProject.toFixed(1)} vs {d.cohortMedian.toFixed(1)})
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── Trajectory sparkline ───────────────────────────────────────────

const TRAJ_W = 480;
const TRAJ_H = 140;
const TRAJ_PAD_T = 10;
const TRAJ_PAD_R = 12;
const TRAJ_PAD_B = 24;
const TRAJ_PAD_L = 36;

function formatTrajDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function TrajectorySparkline({
  trajectory,
  familyColor,
}: {
  trajectory: TrajectoryBlock;
  familyColor: string;
}) {
  if (trajectory.thisProject.length < 2 && trajectory.cohortMedian.length < 2) {
    return null;
  }

  const allPoints = [...trajectory.thisProject, ...trajectory.cohortMedian];
  const allDates = Array.from(new Set(allPoints.map((p) => p.date))).sort();
  if (allDates.length < 2) return null;

  const dateIdx = (d: string) => allDates.indexOf(d);
  const allScores = allPoints.map((p) => p.alphaScore);
  // Pad y-range by 10% on each side so the line doesn't kiss the chart edges.
  const rawMin = Math.min(...allScores);
  const rawMax = Math.max(...allScores);
  const rawRange = rawMax - rawMin || 1;
  const minS = Math.max(0, rawMin - rawRange * 0.1);
  const maxS = rawMax + rawRange * 0.1;
  const range = maxS - minS || 1;

  const plotW = TRAJ_W - TRAJ_PAD_L - TRAJ_PAD_R;
  const plotH = TRAJ_H - TRAJ_PAD_T - TRAJ_PAD_B;
  const xScale = (i: number) => TRAJ_PAD_L + (i / Math.max(allDates.length - 1, 1)) * plotW;
  const yScale = (s: number) => TRAJ_PAD_T + plotH * (1 - (s - minS) / range);

  const buildPath = (points: { date: string; alphaScore: number }[]): string => {
    if (points.length === 0) return "";
    return points
      .map((p, i) => `${i === 0 ? "M" : "L"} ${xScale(dateIdx(p.date))} ${yScale(p.alphaScore)}`)
      .join(" ");
  };

  // 3 horizontal grid lines (top, middle, bottom) for visual anchoring.
  const yTicks = [maxS, (minS + maxS) / 2, minS];
  // allDates.length >= 2 is guaranteed by the early return above.
  const firstDate = formatTrajDate(allDates[0] ?? "");
  const lastDate = formatTrajDate(allDates[allDates.length - 1] ?? "");

  const isGrowingFaster =
    trajectory.growthLabel?.startsWith("Growing faster") ||
    trajectory.growthLabel?.startsWith("Fastest");

  return (
    <div className="peer-cohort-traj">
      <div className="peer-cohort-traj__title">
        90-day trajectory
        {trajectory.growthLabel && (
          <span
            className={
              isGrowingFaster
                ? "peer-cohort-traj__pct peer-cohort-traj__pct--positive"
                : "peer-cohort-traj__pct peer-cohort-traj__pct--negative"
            }
          >
            {trajectory.growthLabel}
          </span>
        )}
      </div>
      <svg
        className="peer-cohort-traj__svg"
        width={TRAJ_W}
        height={TRAJ_H}
        viewBox={`0 0 ${TRAJ_W} ${TRAJ_H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Score trajectory vs cohort median"
      >
        {yTicks.map((tick) => {
          const y = yScale(tick);
          return (
            <g key={`tick-${tick}`}>
              <line
                x1={TRAJ_PAD_L}
                x2={TRAJ_W - TRAJ_PAD_R}
                y1={y}
                y2={y}
                stroke="#1e293b"
                strokeWidth={1}
              />
              <text x={TRAJ_PAD_L - 6} y={y + 3} fontSize={10} fill="#64748b" textAnchor="end">
                {tick.toFixed(0)}
              </text>
            </g>
          );
        })}
        <path
          d={buildPath(trajectory.cohortMedian)}
          fill="none"
          stroke="#94a3b8"
          strokeWidth={2}
          strokeDasharray="4 3"
        />
        <path
          d={buildPath(trajectory.thisProject)}
          fill="none"
          stroke={familyColor}
          strokeWidth={2.5}
        />
        <text x={TRAJ_PAD_L} y={TRAJ_H - 6} fontSize={10} fill="#64748b" textAnchor="start">
          {firstDate}
        </text>
        <text x={TRAJ_W - TRAJ_PAD_R} y={TRAJ_H - 6} fontSize={10} fill="#64748b" textAnchor="end">
          {lastDate}
        </text>
      </svg>
      <div className="peer-cohort-traj__legend">
        <span>
          <svg width="18" height="4" aria-hidden="true">
            <rect width="18" height="3" y="0.5" fill={familyColor} rx="1" />
          </svg>
          this project
        </span>
        <span>
          <svg width="18" height="4" aria-hidden="true">
            <line
              x1="0"
              x2="18"
              y1="2"
              y2="2"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeDasharray="4 3"
            />
          </svg>
          cohort median
        </span>
      </div>
    </div>
  );
}

// ─── Nearest neighbors ──────────────────────────────────────────────

function NearestNeighbors({
  neighbors,
  onOpenProjectDetail,
}: {
  neighbors: NeighborEntry[];
  onOpenProjectDetail?: (project: Project) => void;
}) {
  return (
    <div className="peer-cohort-neighbors">
      <div className="peer-cohort-neighbors__title">Nearest neighbors</div>
      <table className="intel-league peer-cohort-neighbors__table">
        <thead>
          <tr>
            <th>Project</th>
            <th>Score</th>
            <th>Raise</th>
            <th>Family</th>
            <th>Status</th>
            <th>Differentiator</th>
          </tr>
        </thead>
        <tbody>
          {neighbors.map((n) => (
            <tr key={n.id}>
              <td>
                <ProjectNameLink
                  projectId={n.id}
                  label={n.name || n.twitterHandle}
                  onOpenProjectDetail={onOpenProjectDetail}
                />
              </td>
              <td>{n.alphaScore.toFixed(1)}</td>
              <td>{n.fundingRaisedUsd != null ? formatDollars(n.fundingRaisedUsd) : "—"}</td>
              <td>
                {n.family ? (
                  <span
                    className="peer-cohort-neighbors__family"
                    style={{
                      background: FAMILY_COLORS[n.family],
                    }}
                  >
                    {n.family}
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td>
                {n.isLive ? (
                  <span className="peer-cohort-neighbors__status peer-cohort-neighbors__status--live">
                    Live
                  </span>
                ) : n.isArchived ? (
                  <span className="peer-cohort-neighbors__status peer-cohort-neighbors__status--archived">
                    Archived
                  </span>
                ) : n.tgeConfidence != null && n.tgeConfidence >= 50 ? (
                  <span className="peer-cohort-neighbors__status peer-cohort-neighbors__status--approaching">
                    Approaching TGE
                  </span>
                ) : (
                  <span className="peer-cohort-neighbors__status">Pre-TGE</span>
                )}
              </td>
              <td className="peer-cohort-neighbors__diff">{n.topDifferentiator}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function formatDollars(usd: number): string {
  if (usd >= 1e9) return `$${(usd / 1e9).toFixed(2)}B`;
  if (usd >= 1e6) return `$${(usd / 1e6).toFixed(1)}M`;
  if (usd >= 1e3) return `$${(usd / 1e3).toFixed(0)}K`;
  return `$${usd.toFixed(0)}`;
}

/**
 * Color class for the residual chip: green when undervalued (raised
 * less than predicted), amber when overvalued, neutral when "in line"
 * (less than $100k delta). Mirrors the label content from
 * `formatResidualLabel` — without the neutral case, residual=0 would
 * render the "In line with cohort" string in amber/overvalued tone.
 */
function residualClass(usd: number | null): string {
  if (usd == null || Math.abs(usd) < 100_000) return "peer-cohort-summary__residual";
  if (usd < 0) return "peer-cohort-summary__residual peer-cohort-summary__residual--under";
  return "peer-cohort-summary__residual peer-cohort-summary__residual--over";
}

/** Renders a 0–100 percentile (0 = top, 100 = bottom) as "top X%" / "bottom X%". */
function percentileLabel(pct: number): string {
  if (pct <= 1) return "top of cohort";
  if (pct >= 99) return "bottom of cohort";
  // Snap the median band to a neutral phrase so 49.6 ("top 50%") and
  // 50.4 ("bottom 50%") don't flip across what's effectively the same
  // position. Without this, the asymmetry around exactly 50 is jarring.
  if (pct >= 49 && pct <= 51) return "median of cohort";
  if (pct < 50) return `top ${Math.max(1, Math.round(pct))}%`;
  return `bottom ${Math.max(1, Math.round(100 - pct))}%`;
}
